import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import OgShell from '../components/OgShell';
import AuthGate from '../components/AuthGate';
import {
  addQiCaseAddendum,
  confirmQiCaseByPractitioner,
  createSession,
  deleteQiMedia,
  disputeQiCase,
  fetchCase,
  fetchConsent,
  fetchFollowups,
  fetchMedia,
  fetchNotifications,
  getQiErrorMessage,
  fetchReview,
  fetchRevisions,
  fetchSessions,
  fetchSessionRecords,
  getSignedMediaUrl,
  notifyRecipient,
  publishWitness,
  regenerateRecipientToken,
  requestQiCaseDeletion,
  revokeRecipientToken,
  sendQiEmail,
  saveReview,
  setMediaPermission,
  uploadQiMedia,
  updateQiCaseFields,
  updateQiSession,
  withdrawQiCaseDeletion,
} from '../lib/api';
import { CASE_STATUS_LABELS, FOLLOWUP_LABELS, buildAnonymousCaseSummary, formatDate } from '../lib/constants';
import type {
  EvidenceStage,
  QiCase,
  QiConsent,
  QiFollowup,
  QiMedia,
  QiNotification,
  QiReview,
  QiRevision,
  QiRole,
  QiSession,
  QiSessionRecord,
} from '../lib/types';

const EVIDENCE_STAGE_OPTIONS: { value: EvidenceStage; label: string }[] = [
  { value: 'before', label: '施炁前' },
  { value: 'during', label: '施炁當下' },
  { value: 'after', label: '施炁後' },
  { value: 'followup', label: '追蹤' },
];

const DELETION_REASONS = ['建立錯誤', '重複案例', '受施者錯誤', '不應屬於本人', '隱私顧慮', '雙方爭議', '測試資料', '其他'];

// qi_cases 上關於刪除／爭議的治理欄位（fetchCase 以 select('*') 抓取，運行時皆已存在，此型別僅補齊型別標注）。
interface QiCaseGovernance extends QiCase {
  deletion_status?: string | null;
  deletion_reason_category?: string | null;
  deletion_reason?: string | null;
  deletion_requested_by?: string | null;
  deletion_requested_at?: string | null;
  pre_deletion_status?: string | null;
  publication_paused_at?: string | null;
  purged_at?: string | null;
  disputed?: boolean;
  disputed_at?: string | null;
  disputed_reason?: string | null;
}

export default function CaseDetailPage() {
  const { id } = useParams();
  const caseId = Number(id);

  return (
    <OgShell>
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <AuthGate>
          {(auth) => <CaseDetail caseId={caseId} userId={auth.userId} role={auth.role} />}
        </AuthGate>
      </div>
    </OgShell>
  );
}

function CaseDetail({ caseId, userId, role }: { caseId: number; userId: string; role: QiRole }) {
  const [qCase, setQCase] = useState<QiCaseGovernance | null>(null);
  const [sessions, setSessions] = useState<QiSession[]>([]);
  const [records, setRecords] = useState<QiSessionRecord[]>([]);
  const [revisions, setRevisions] = useState<QiRevision[]>([]);
  const [followups, setFollowups] = useState<QiFollowup[]>([]);
  const [consent, setConsent] = useState<QiConsent | null>(null);
  const [media, setMedia] = useState<QiMedia[]>([]);
  const [notifications, setNotifications] = useState<QiNotification[]>([]);
  const [mediaUrls, setMediaUrls] = useState<Record<number, string>>({});
  const [review, setReview] = useState<QiReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [mediaDragging, setMediaDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [recipientToken, setRecipientToken] = useState<string | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [recipientEmailDraft, setRecipientEmailDraft] = useState('');
  const [deletingMediaId, setDeletingMediaId] = useState<number | null>(null);
  const [showCorrection, setShowCorrection] = useState(false);
  const [correctionDate, setCorrectionDate] = useState('');
  const [correctionLocation, setCorrectionLocation] = useState('');
  const [addendumText, setAddendumText] = useState('');
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [addendumSaving, setAddendumSaving] = useState(false);

  // 案例治理（刪除／爭議申請）
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [deleteReasonCategory, setDeleteReasonCategory] = useState('');
  const [deleteReasonNote, setDeleteReasonNote] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeWhichData, setDisputeWhichData] = useState('');
  const [disputeResolution, setDisputeResolution] = useState('');
  const [governanceSubmitting, setGovernanceSubmitting] = useState(false);
  const [publicSummary, setPublicSummary] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // 新增施炁表單
  const [showNewSession, setShowNewSession] = useState(false);
  const [newSessionDate, setNewSessionDate] = useState('');
  const [newSessionLocation, setNewSessionLocation] = useState('');
  const [newSessionMethod, setNewSessionMethod] = useState('');
  const [newSessionObs, setNewSessionObs] = useState('');
  const [addingSession, setAddingSession] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editingSessionDate, setEditingSessionDate] = useState('');
  const [savingSessionTime, setSavingSessionTime] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await fetchCase(caseId);
      if (!c) {
        setError('找不到此案例，或你沒有權限查看。');
        return;
      }
      setQCase(c);
      setRecipientEmailDraft(c.recipient_email || '');
      setCorrectionDate(c.session_datetime ? c.session_datetime.slice(0, 16) : '');
      setCorrectionLocation(c.location || '');
      const [s, r, history, f, con, m, n, rev] = await Promise.all([
        fetchSessions(caseId),
        fetchSessionRecords(caseId),
        fetchRevisions(caseId),
        fetchFollowups(caseId),
        fetchConsent(caseId),
        fetchMedia(caseId),
        fetchNotifications(caseId),
        fetchReview(caseId),
      ]);
      setSessions(s);
      setRecords(r);
      setRevisions(history);
      setFollowups(f);
      setConsent(con);
      setMedia(m);
      setNotifications(n);
      setReview(rev);
      setPublicSummary(rev?.public_summary || buildAnonymousCaseSummary(c, r, f));

      const urlMap: Record<number, string> = {};
      await Promise.all(
        m.map(async (item) => {
          const url = await getSignedMediaUrl(item.file_path);
          if (url) urlMap[item.id] = url;
        }),
      );
      setMediaUrls(urlMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  const isRecipient = userId === qCase?.recipient_id || userId === qCase?.case_owner_id;

  // ── 案例治理狀態與權限 ──
  const deletionStatus = qCase?.deletion_status ?? null;
  const isArchived = qCase?.status === 'archived' || deletionStatus === 'deleted' || !!qCase?.purged_at;
  const isDeletionRequested = deletionStatus === 'deletion_requested';
  const isDisputed = !!qCase?.disputed;
  const isPublicationPaused = !!qCase?.publication_paused_at;
  const lockEditing = isArchived || isDeletionRequested;
  const isAdmin = role === 'admin';
  const isStaff = role === 'admin' || role === 'reviewer';
  const isActor = !!(qCase && (userId === qCase.case_owner_id || userId === qCase.practitioner_id || userId === qCase.recipient_id || isAdmin));
  const canWithdraw = isDeletionRequested && (qCase?.deletion_requested_by === userId || isAdmin);
  const withinCorrectionWindow = !!qCase && Date.now() - new Date(qCase.created_at).getTime() <= 48 * 60 * 60 * 1000;
  const reviewNotStarted = !review || review.review_status === 'pending';
  const canPublishAnonymous = !!qCase && !isArchived && !isDeletionRequested && !isDisputed
    && role !== 'candidate'
    && (userId === qCase.practitioner_id || isStaff);
  const canCorrectOriginal = isActor && withinCorrectionWindow && reviewNotStarted && !isArchived && !isDeletionRequested;
  const canEditSessionTime = !lockEditing && !!qCase && (userId === qCase.practitioner_id || isAdmin);
  const isPractitionerConfirmed = !!qCase?.practitioner_confirmed_at || ['under_review', 'approved_anonymous', 'published'].includes(qCase?.status || '');

  const statusBadges: { label: string; cls: string }[] = [];
  if (isArchived) statusBadges.push({ label: '已歸檔', cls: 'bg-foreground-200 text-foreground-600' });
  if (isDeletionRequested) statusBadges.push({ label: '刪除審核中', cls: 'bg-amber-100 text-amber-700' });
  if (isDisputed) statusBadges.push({ label: '有爭議', cls: 'bg-rose-100 text-rose-700' });
  if (isPublicationPaused) statusBadges.push({ label: '公開暫停', cls: 'bg-secondary-100 text-secondary-900' });

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || !qCase) return;
      setUploading(true);
      setError(null);
      try {
        const firstSessionId = sessions.length > 0 ? sessions[0].id : null;
        for (const file of files) {
          await uploadQiMedia(file, qCase.id, 'post_condition', userId, false, {
            sessionId: firstSessionId,
            evidenceStage: 'after',
          });
        }
        await load();
        setNotice('檔案已上傳，原始檔存於私人空間，尚未同意公開。');
      } catch (err) {
        setError(err instanceof Error ? err.message : '上傳失敗');
      } finally {
        setUploading(false);
      }
    },
    [qCase, userId, load, sessions],
  );

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      await uploadFiles(files);
    },
    [uploadFiles],
  );

  const handleMediaDrop = useCallback(
    async (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setMediaDragging(false);
      if (uploading) return;
      await uploadFiles(Array.from(e.dataTransfer.files));
    },
    [uploadFiles, uploading],
  );

  const handleAddSession = useCallback(async () => {
    if (!qCase) return;
    if (!newSessionDate) {
      setError('請填寫施炁日期與時間（最小必填）。');
      return;
    }
    setAddingSession(true);
    setError(null);
    try {
      await createSession(qCase.id, {
        session_datetime: newSessionDate,
        location: newSessionLocation,
        method: newSessionMethod,
        practitioner_observation: newSessionObs,
      });
      setShowNewSession(false);
      setNewSessionDate('');
      setNewSessionLocation('');
      setNewSessionMethod('');
      setNewSessionObs('');
      setNotice('已新增一次施炁紀錄，並自動產生 7／30／90 日追蹤。');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '新增施炁失敗');
    } finally {
      setAddingSession(false);
    }
  }, [qCase, newSessionDate, newSessionLocation, newSessionMethod, newSessionObs, load]);

  const beginEditSessionTime = useCallback((session: QiSession) => {
    setEditingSessionId(session.id);
    setEditingSessionDate(session.session_datetime ? session.session_datetime.slice(0, 16) : '');
    setError(null);
    setNotice(null);
  }, []);

  const handleSaveSessionTime = useCallback(async () => {
    if (!editingSessionId || !editingSessionDate || !canEditSessionTime) {
      setError('請重新選擇正確的施炁日期與時間。');
      return;
    }
    setSavingSessionTime(true);
    setError(null);
    try {
      await updateQiSession(
        editingSessionId,
        { session_datetime: new Date(editingSessionDate).toISOString() },
        '施炁者更正誤登的施炁日期與時間',
      );
      setEditingSessionId(null);
      setEditingSessionDate('');
      setNotice('施炁時間已更新；相關 7／30／90 日追蹤日期已同步重排，原時間保留於修訂歷程。');
      await load();
    } catch (err) {
      setError(getQiErrorMessage(err, '更正施炁時間失敗'));
    } finally {
      setSavingSessionTime(false);
    }
  }, [editingSessionId, editingSessionDate, canEditSessionTime, load]);

  const handleNotify = useCallback(async () => {
    if (!qCase) return;
    setError(null);
    setNotice(null);
    try {
      const res = await notifyRecipient(qCase.id, false);
      if (res.recipient_identified) {
        setNotice('站內通知已建立並送達。若需 Email 通知，請使用「重新寄送 Email」。');
      } else {
        setNotice('受施者尚未綁定會員帳號，請複製安全連結私下傳送給案主。');
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '通知失敗');
    }
  }, [qCase, load]);

  const handleRegenerate = useCallback(async () => {
    if (!qCase) return;
    try {
      const res = await regenerateRecipientToken(qCase.id);
      setRecipientToken(res.token);
      setNotice('安全連結已重新產生（舊連結立即失效）。');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '重新產生失敗');
    }
  }, [qCase, load]);

  const handleResendEmail = useCallback(async () => {
    if (!qCase) return;
    const email = recipientEmailDraft.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('請先輸入正確的受施者 Email。');
      return;
    }
    setEmailSending(true);
    setError(null);
    try {
      if (email !== (qCase.recipient_email || '').toLowerCase()) {
        await updateQiCaseFields(qCase.id, { recipient_email: email }, '更正受施者 Email 並重新寄送通知');
        setQCase((current) => current ? { ...current, recipient_email: email } : current);
      }
      let activeToken = recipientToken;
      if (!activeToken) {
        const tokenResult = await regenerateRecipientToken(qCase.id);
        activeToken = tokenResult.token;
        setRecipientToken(activeToken);
      }
      const notify = await notifyRecipient(qCase.id, true);
      if (notify.email_notification_id) {
        const basePath = (__BASE_PATH__ as string).split('/').filter(Boolean).join('/');
        const pathPrefix = basePath ? `/${basePath}` : '';
        const secureUrl = `${window.location.origin}${pathPrefix}/og/qi-healing/follow-up/${activeToken}`;
        const res = await sendQiEmail({
          notification_id: notify.email_notification_id,
          recipient_email: email,
          recipient_alias: qCase.recipient_alias || undefined,
          secure_url: secureUrl,
        });
        if (res.email_status === 'sent') {
          setNotice('Email 已寄出。');
        } else if (res.reason === 'email_service_not_configured') {
          setNotice('站內通知已建立；Email 服務尚未配置，尚未寄出。');
        } else {
          setNotice(`站內通知已建立；Email 尚未寄出：${res.reason || '寄送失敗'}。`);
        }
      } else if (notify.email_status === 'missing_email') {
        setNotice('站內通知已建立；Email 未寄出：尚未填寫受施者 Email。');
      } else {
        setNotice('站內通知已建立；未寄送 Email。');
      }
      // 只更新通知狀態，避免寄送後整頁重載並跳回頁首。
      setNotifications(await fetchNotifications(qCase.id));
    } catch (err) {
      setError(getQiErrorMessage(err, 'Email 寄送失敗'));
    } finally {
      setEmailSending(false);
    }
  }, [qCase, recipientEmailDraft, recipientToken]);

  const handleSaveEmail = useCallback(async () => {
    if (!qCase) return;
    const email = recipientEmailDraft.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('請輸入正確的受施者 Email。');
      return;
    }
    setEmailSaving(true);
    setError(null);
    try {
      await updateQiCaseFields(qCase.id, { recipient_email: email }, '更正受施者 Email');
      setQCase((current) => current ? { ...current, recipient_email: email } : current);
      setNotice('受施者 Email 已更新；可按「重新寄送 Email」。');
    } catch (err) {
      setError(getQiErrorMessage(err, 'Email 更新失敗'));
    } finally {
      setEmailSaving(false);
    }
  }, [qCase, recipientEmailDraft]);

  const handleDeleteMedia = useCallback(async (mediaId: number) => {
    if (!window.confirm('確定刪除這個附件？刪除後將不再顯示於案例中。')) return;
    setDeletingMediaId(mediaId);
    setError(null);
    try {
      await deleteQiMedia(mediaId);
      setMedia((items) => items.filter((item) => item.id !== mediaId));
      setMediaUrls((urls) => {
        const next = { ...urls };
        delete next[mediaId];
        return next;
      });
      setNotice('附件已刪除。');
    } catch (err) {
      setError(err instanceof Error ? err.message : '附件刪除失敗');
    } finally {
      setDeletingMediaId(null);
    }
  }, []);

  const handleSaveCorrection = useCallback(async () => {
    if (!qCase || !canCorrectOriginal) return;
    if (!correctionDate) {
      setError('請填寫正確的施炁日期與時間。');
      return;
    }
    setCorrectionSaving(true);
    setError(null);
    try {
      const changes = {
        session_datetime: new Date(correctionDate).toISOString(),
        location: correctionLocation.trim(),
      };
      await updateQiCaseFields(qCase.id, changes, '送出後 48 小時內更正案例基本資料');
      if (sessions[0]) {
        await updateQiSession(sessions[0].id, changes, '送出後 48 小時內更正施炁基本資料');
      }
      setNotice('案例基本資料已更正，原值已保留於修訂紀錄。');
      setShowCorrection(false);
      await load();
    } catch (err) {
      setError(getQiErrorMessage(err, '更正案例失敗'));
    } finally {
      setCorrectionSaving(false);
    }
  }, [qCase, canCorrectOriginal, correctionDate, correctionLocation, sessions, load]);

  const handleAddendum = useCallback(async () => {
    if (!qCase || addendumText.trim().length < 2) {
      setError('請輸入要補充或更正的內容。');
      return;
    }
    setAddendumSaving(true);
    setError(null);
    try {
      await addQiCaseAddendum(qCase.id, addendumText.trim());
      setAddendumText('');
      setNotice('補充內容已加入時間軸，原始紀錄沒有被覆蓋。');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '新增補充失敗');
    } finally {
      setAddendumSaving(false);
    }
  }, [qCase, addendumText, load]);

  const handleRevoke = useCallback(async () => {
    if (!qCase) return;
    try {
      await revokeRecipientToken(qCase.id);
      setNotice('安全連結已撤銷。');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '撤銷失敗');
    }
  }, [qCase, load]);

  const handleMediaPatch = useCallback(
    async (mediaId: number, patch: Parameters<typeof setMediaPermission>[1]) => {
      // 先局部更新畫面；不要重新 load 整個案例，否則使用者每勾一個選項
      // 都會被送回頁首，正在播放的影片也會被中斷。
      const previous = media.find((item) => item.id === mediaId);
      setMedia((items) => items.map((item) => (
        item.id === mediaId ? { ...item, ...patch } : item
      )));
      try {
        await setMediaPermission(mediaId, patch);
        setNotice('附件設定已儲存。');
      } catch (err) {
        if (previous) {
          setMedia((items) => items.map((item) => (
            item.id === mediaId ? previous : item
          )));
        }
        setError(err instanceof Error ? err.message : '更新媒體授權失敗');
      }
    },
    [media],
  );

  const handleRequestDeletion = useCallback(async () => {
    if (!qCase) return;
    if (!deleteReasonCategory) { setModalError('請選擇刪除理由。'); return; }
    if (!deleteReasonNote.trim()) { setModalError('請填寫補充說明。'); return; }
    if (!deleteConfirm) { setModalError('請勾選二次確認後再送出。'); return; }
    setGovernanceSubmitting(true);
    setModalError(null);
    try {
      const res = await requestQiCaseDeletion(qCase.id, deleteReasonCategory, deleteReasonNote.trim());
      if (!res.ok) { setModalError(res.error || '申請失敗'); return; }
      setShowDeleteModal(false);
      setDeleteReasonCategory('');
      setDeleteReasonNote('');
      setDeleteConfirm(false);
      setNotice(qCase.status === 'published'
        ? '已提出刪除申請，案例進入刪除審核中；公開見證已暫停。'
        : '已提出刪除申請，案例進入刪除審核中。');
      await load();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : '申請失敗');
    } finally {
      setGovernanceSubmitting(false);
    }
  }, [qCase, deleteReasonCategory, deleteReasonNote, deleteConfirm, load]);

  const handleWithdrawDeletion = useCallback(async () => {
    if (!qCase) return;
    setGovernanceSubmitting(true);
    setError(null);
    try {
      const res = await withdrawQiCaseDeletion(qCase.id);
      if (!res.ok) { setError(res.error || '撤回失敗'); return; }
      setNotice('已撤回刪除申請，案例恢復正常狀態。');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '撤回失敗');
    } finally {
      setGovernanceSubmitting(false);
    }
  }, [qCase, load]);

  const handleDispute = useCallback(async () => {
    if (!qCase) return;
    if (!disputeReason.trim()) { setModalError('請填寫爭議原因。'); return; }
    const parts = [disputeReason.trim()];
    if (disputeWhichData.trim()) parts.push(`問題段落：${disputeWhichData.trim()}`);
    if (disputeResolution.trim()) parts.push(`期望處理：${disputeResolution.trim()}`);
    setGovernanceSubmitting(true);
    setModalError(null);
    try {
      const res = await disputeQiCase(qCase.id, parts.join('；'));
      if (!res.ok) { setModalError(res.error || '提出異議失敗'); return; }
      setShowDisputeModal(false);
      setDisputeReason('');
      setDisputeWhichData('');
      setDisputeResolution('');
      setNotice('已提出紀錄爭議，案例有待處理的異議，公開已暫停。');
      await load();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : '提出異議失敗');
    } finally {
      setGovernanceSubmitting(false);
    }
  }, [qCase, disputeReason, disputeWhichData, disputeResolution, load]);

  const basePath = (__BASE_PATH__ as string).split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  const recipientLink = recipientToken
    ? `${window.location.origin}${pathPrefix}/og/qi-healing/follow-up/${recipientToken}`
    : '';

  const copyLink = async () => {
    if (!recipientLink) return;
    try {
      await navigator.clipboard.writeText(recipientLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handlePractitionerPublish = useCallback(async () => {
    if (!qCase) return;
    setPublishing(true);
    setError(null);
    try {
      const summary = publicSummary.trim() || buildAnonymousCaseSummary(qCase, records, followups);
      setPublicSummary(summary);
      await confirmQiCaseByPractitioner(qCase.id);
      if (isStaff) {
        await saveReview(qCase.id, userId, {
          review_status: 'approved',
          evidence_level: qCase.evidence_level || 'E0',
          public_summary: summary,
          internal_notes: review?.internal_notes || '',
        });
      }
      const result = await publishWitness(qCase.id, '匿名見證', summary, qCase.evidence_level || 'E0');
      if (result.action === 'submit_first_review') {
        setNotice('這是同一主訴的首次公開案例，已送交ABAA審核。');
      } else {
        setNotice('匿名文字案例已發布；照片、聲音及影片仍依各檔案授權處理。');
      }
      await load();
    } catch (err) {
      setError(getQiErrorMessage(err, '發布失敗'));
    } finally {
      setPublishing(false);
    }
  }, [qCase, isStaff, userId, publicSummary, review, records, followups, load]);

  if (loading) {
    return (
      <div className="py-20 text-center">
        <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !qCase) {
    return (
      <div className="py-16 text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-foreground-100 flex items-center justify-center">
          <i className="ri-lock-line text-foreground-400 text-2xl" />
        </div>
        <p className="text-sm text-foreground-500">{error}</p>
        <Link
          to="/og/qi-healing"
          className="whitespace-nowrap inline-flex px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 cursor-pointer"
        >
          回到宇炁療育
        </Link>
      </div>
    );
  }

  if (!qCase) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/og/qi-healing" className="text-sm text-foreground-500 hover:text-accent-600 cursor-pointer transition-colors">
          ← 回到我的紀錄
        </Link>
        <span className="text-xs px-3 py-1 rounded-full bg-background-200 text-foreground-600 whitespace-nowrap">
          {CASE_STATUS_LABELS[qCase.status] || qCase.status}
        </span>
      </div>

      <header className="space-y-1">
        <h1 className="text-xl md:text-2xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
          {qCase.case_code || `案例 #${qCase.id}`}
        </h1>
        <p className="text-sm text-foreground-500">
          {qCase.recipient_alias || '未填稱呼'} &middot; {formatDate(qCase.session_datetime)} &middot; {qCase.location || '未填地點'}
        </p>
      </header>

      {/* 案例治理狀態 */}
      <div className="flex items-center gap-2 flex-wrap">
        {statusBadges.length === 0 ? (
          <span className="text-xs px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">正常</span>
        ) : (
          statusBadges.map((b) => (
            <span key={b.label} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${b.cls}`}>{b.label}</span>
          ))
        )}
      </div>

      {error && <p className="text-sm text-rose-600 bg-rose-50 rounded-md px-4 py-2.5">{error}</p>}
      {notice && <p className="text-sm text-emerald-700 bg-emerald-50 rounded-md px-4 py-2.5">{notice}</p>}

      {/* 案例治理動作 */}
      {isActor && (
        <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 space-y-3" style={{ order: 80 }}>
          <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>案例治理</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {canCorrectOriginal && (
              <button
                type="button"
                onClick={() => setShowCorrection((shown) => !shown)}
                className="whitespace-nowrap px-3 py-2 rounded-md border border-accent-300 text-accent-700 text-xs hover:bg-accent-50 transition-colors cursor-pointer"
              >
                更正案例基本資料
              </button>
            )}
            {!isDeletionRequested && !isArchived && (
              <button
                onClick={() => { setModalError(null); setShowDeleteModal(true); }}
                className="whitespace-nowrap px-3 py-2 rounded-md border border-rose-300 text-rose-600 text-xs hover:bg-rose-50 transition-colors cursor-pointer"
              >
                申請刪除整個案例
              </button>
            )}
            {canWithdraw && (
              <button
                onClick={handleWithdrawDeletion}
                disabled={governanceSubmitting}
                className="whitespace-nowrap px-3 py-2 rounded-md border border-foreground-200 text-foreground-600 text-xs hover:bg-foreground-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                {governanceSubmitting ? '處理中…' : '撤回刪除申請'}
              </button>
            )}
            {!isArchived && (
              <button
                onClick={() => { setModalError(null); setShowDisputeModal(true); }}
                className="whitespace-nowrap px-3 py-2 rounded-md border border-amber-300 text-amber-700 text-xs hover:bg-amber-50 transition-colors cursor-pointer"
              >
                對此案例提出異議
              </button>
            )}
          </div>
          {canCorrectOriginal ? (
            <p className="text-xs text-foreground-500">送出後 48 小時內且 Reviewer 尚未開始處理，可更正日期與地點；原值仍保留於修訂紀錄。</p>
          ) : !isArchived && (
            <p className="text-xs text-foreground-500">原始資料已鎖定；如需說明，請使用下方「新增更正／補充」。</p>
          )}

          {showCorrection && canCorrectOriginal && (
            <div className="bg-background-50 border border-accent-200 rounded-lg p-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-xs text-foreground-600 space-y-1">
                  <span>施炁日期與時間</span>
                  <input
                    type="datetime-local"
                    value={correctionDate}
                    onChange={(e) => setCorrectionDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm"
                  />
                </label>
                <label className="text-xs text-foreground-600 space-y-1">
                  <span>地點</span>
                  <input
                    type="text"
                    value={correctionLocation}
                    onChange={(e) => setCorrectionLocation(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm"
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowCorrection(false)} className="px-3 py-2 text-xs border border-foreground-200 rounded-md cursor-pointer">取消</button>
                <button type="button" onClick={handleSaveCorrection} disabled={correctionSaving} className="px-3 py-2 text-xs bg-accent-500 text-background-50 rounded-md cursor-pointer disabled:opacity-50">
                  {correctionSaving ? '儲存中…' : '確認更正'}
                </button>
              </div>
            </div>
          )}

          {!isArchived && !isDeletionRequested && (
            <div className="bg-background-50 border border-foreground-200/70 rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-foreground-700">新增更正／補充</p>
              <p className="text-xs text-foreground-500">補充內容會加在案例時間軸，不會覆蓋已送出的原始紀錄。</p>
              <textarea
                value={addendumText}
                onChange={(e) => setAddendumText(e.target.value)}
                rows={3}
                placeholder="例如：更正受施者當時的描述，或補充送出後取得的新資訊……"
                className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm resize-y"
              />
              <div className="flex justify-end">
                <button type="button" onClick={handleAddendum} disabled={addendumSaving || addendumText.trim().length < 2} className="px-3 py-2 text-xs bg-primary-500 text-background-50 rounded-md cursor-pointer disabled:opacity-50">
                  {addendumSaving ? '加入中…' : '加入案例時間軸'}
                </button>
              </div>
            </div>
          )}
          {isDeletionRequested && (
            <p className="text-xs text-amber-700">此案例正在刪除審核中，暫停新增施炁與再次申請刪除，仍可查看現有資料與版本記錄。</p>
          )}
          {isArchived && (
            <p className="text-xs text-foreground-500">此案例已歸檔，仍可查看現有資料與版本記錄。</p>
          )}
          {isDisputed && (
            <p className="text-xs text-rose-600">此案例有尚未處理的異議，公開已暫停，雙方原始內容均被保留。</p>
          )}
        </section>
      )}

      {/* 受施者通知與安全連結 */}
      <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 space-y-3" style={{ order: 40 }}>
        <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
          通知案主與安全連結
        </h2>

        {/* 通知狀態：站內 / Email 分開顯示 */}
        <NotificationStatus notifications={notifications} />

        <div>
          <p className="text-xs text-foreground-500 mb-1">受施者 Email（若填錯可在此更正）</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="email"
              value={recipientEmailDraft}
              onChange={(e) => setRecipientEmailDraft(e.target.value)}
              placeholder="name@example.com"
              className="flex-1 min-w-[240px] px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-700"
            />
            <button
              type="button"
              onClick={handleSaveEmail}
              disabled={emailSaving}
              className="whitespace-nowrap px-3 py-2 rounded-md border border-accent-400 text-xs text-accent-700 hover:bg-accent-50 transition-colors cursor-pointer disabled:opacity-50"
            >
              {emailSaving ? '儲存中…' : '儲存 Email'}
            </button>
          </div>
        </div>

        <div>
          <p className="text-xs text-foreground-400 mb-1">安全回報連結（僅在產生或重新產生時顯示一次）</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              readOnly
              value={recipientLink}
              placeholder="尚未取得，請點擊「重新產生連結」"
              className="flex-1 min-w-[200px] px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-xs text-foreground-600"
            />
            <button
              onClick={copyLink}
              disabled={!recipientLink}
              className="whitespace-nowrap px-3 py-2 rounded-md bg-accent-500 text-background-50 text-xs hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {copied ? '已複製' : '複製備用連結'}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleNotify}
            className="whitespace-nowrap px-3 py-2 rounded-md bg-primary-500 text-background-50 text-xs hover:bg-primary-600 transition-colors cursor-pointer"
          >
            通知案主補充（站內）
          </button>
          <button
            onClick={handleResendEmail}
            disabled={emailSending}
            className="whitespace-nowrap px-3 py-2 rounded-md border border-foreground-200 text-xs text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer disabled:opacity-50"
          >
            {emailSending ? '寄送中…' : '重新寄送 Email'}
          </button>
          <button
            onClick={handleRegenerate}
            className="whitespace-nowrap px-3 py-2 rounded-md border border-foreground-200 text-xs text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer"
          >
            重新產生連結
          </button>
          <button
            onClick={handleRevoke}
            className="whitespace-nowrap px-3 py-2 rounded-md border border-foreground-200 text-xs text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer"
          >
            撤銷連結
          </button>
        </div>
      </section>

      {/* 多次施炁 */}
      <section
        style={{ order: 50 }}
        className={`space-y-3 rounded-xl border-2 border-dashed p-4 transition-colors ${
          mediaDragging ? 'border-accent-500 bg-accent-50/60' : 'border-foreground-200/70'
        }`}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!uploading) setMediaDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setMediaDragging(false);
        }}
        onDrop={handleMediaDrop}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
            施炁紀錄（{sessions.length} 次）
          </h2>
          <button
            onClick={() => setShowNewSession((v) => !v)}
            disabled={lockEditing}
            className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-500 text-background-50 text-xs hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {lockEditing ? '已鎖定' : showNewSession ? '取消' : '新增一次施炁紀錄'}
          </button>
        </div>

        {showNewSession && (
          <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 space-y-3">
            <div>
              <p className="text-sm text-foreground-700">施炁日期與時間 <span className="text-rose-500">*</span></p>
              <input
                type="datetime-local"
                value={newSessionDate}
                onChange={(e) => setNewSessionDate(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800"
              />
            </div>
            <div>
              <p className="text-sm text-foreground-700">地點／遠距方式</p>
              <input
                type="text"
                value={newSessionLocation}
                onChange={(e) => setNewSessionLocation(e.target.value)}
                placeholder="可留白"
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800"
              />
            </div>
            <div>
              <p className="text-sm text-foreground-700">方式</p>
              <input
                type="text"
                value={newSessionMethod}
                onChange={(e) => setNewSessionMethod(e.target.value)}
                placeholder="可留白"
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800"
              />
            </div>
            <div>
              <p className="text-sm text-foreground-700">施炁者觀察</p>
              <textarea
                value={newSessionObs}
                onChange={(e) => setNewSessionObs(e.target.value)}
                rows={3}
                placeholder="可留白"
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 resize-none"
              />
            </div>
            <button
              onClick={handleAddSession}
              disabled={addingSession}
              className="whitespace-nowrap px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
            >
              {addingSession ? '新增中…' : '儲存施炁紀錄'}
            </button>
          </div>
        )}

        {sessions.length === 0 ? (
          <p className="text-sm text-foreground-400 bg-background-100 border border-foreground-200/50 rounded-xl p-4">尚無施炁紀錄。</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} className="bg-background-100 border border-foreground-200/50 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm font-medium text-foreground-800">
                    第 {s.session_number} 次施炁 &middot; {formatDate(s.session_datetime)}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {s.method && <span className="text-xs px-2.5 py-1 rounded-full bg-background-200 text-foreground-600">{s.method}</span>}
                    {canEditSessionTime && (
                      <button
                        type="button"
                        onClick={() => beginEditSessionTime(s)}
                        className="whitespace-nowrap px-3 py-1.5 rounded-md border border-accent-300 text-accent-700 text-xs hover:bg-accent-50 transition-colors cursor-pointer"
                      >
                        更正時間
                      </button>
                    )}
                  </div>
                </div>
                {editingSessionId === s.id && (
                  <div className="mt-3 rounded-lg border border-accent-200 bg-background-50 p-3 space-y-2">
                    <label className="block text-xs text-foreground-600">
                      <span>更正誤登的施炁日期與時間</span>
                      <input
                        type="datetime-local"
                        value={editingSessionDate}
                        onChange={(e) => setEditingSessionDate(e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800"
                      />
                    </label>
                    <p className="text-xs text-foreground-500">此功能僅供修正誤登時間；儲存後，該次 7／30／90 日追蹤日期會自動同步重排。</p>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => { setEditingSessionId(null); setEditingSessionDate(''); }}
                        disabled={savingSessionTime}
                        className="px-3 py-2 text-xs border border-foreground-200 rounded-md cursor-pointer disabled:opacity-50"
                      >
                        取消
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveSessionTime}
                        disabled={savingSessionTime || !editingSessionDate}
                        className="px-3 py-2 text-xs bg-accent-500 text-background-50 rounded-md cursor-pointer disabled:opacity-50"
                      >
                        {savingSessionTime ? '儲存中…' : '儲存新時間'}
                      </button>
                    </div>
                  </div>
                )}
                {s.location && <p className="text-xs text-foreground-400 mt-1">{s.location}</p>}
                {s.practitioner_observation && <p className="text-sm text-foreground-700 mt-2">{s.practitioner_observation}</p>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 時間軸（按 record_stage） */}
      <TimelineSection order={60} title="施炁前" records={records.filter((r) => r.record_stage === 'pre_condition')} />
      <TimelineSection order={61} title="施炁當下" records={records.filter((r) => r.record_stage === 'method' || r.record_stage === 'immediate_reaction')} />
      <TimelineSection order={62} title="施炁後（24 小時內）" records={records.filter((r) => r.record_stage === 'post_condition')} />
      <TimelineSection order={63} title="受施者第一視角" records={records.filter((r) => r.record_stage === 'recipient_first_person')} />
      <TimelineSection order={64} title="受施者回報" records={records.filter((r) => r.record_stage === 'recipient_report')} />
      <TimelineSection order={90} title="送出後更正／補充" records={records.filter((r) => r.record_stage === 'case_addendum')} />

      {revisions.length > 0 && (
        <section className="space-y-3" style={{ order: 100 }}>
          <div>
            <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
              修訂歷程（原始內容永久保留）
            </h2>
            <p className="mt-1 text-xs text-foreground-500">
              每次修正均保存修改前與修改後內容，不會以新資料覆蓋歷史版本。
            </p>
          </div>
          {revisions.map((revision) => (
            <RevisionCard key={revision.id} revision={revision} />
          ))}
        </section>
      )}

      {/* 追蹤 */}
      <section className="space-y-3" style={{ order: 10 }}>
        <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
          後續追蹤
        </h2>
        {followups.length === 0 ? (
          <p className="text-sm text-foreground-400 bg-background-100 border border-foreground-200/50 rounded-xl p-4">尚未建立追蹤任務。</p>
        ) : (
          <div className="space-y-2">
            {followups.map((f) => (
              <div key={f.id} className="bg-background-100 border border-foreground-200/50 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm font-medium text-foreground-800">
                    {FOLLOWUP_LABELS[f.followup_type] || f.followup_type}
                    {f.session_id ? `（第 ${sessions.find((s) => s.id === f.session_id)?.session_number ?? ''} 次）` : ''}
                  </p>
                  <FollowupBadge status={f.status} />
                </div>
                <p className="text-xs text-foreground-400 mt-1">到期日：{f.due_date || '—'}</p>
                {f.self_report && <p className="text-sm text-foreground-700 mt-2">{f.self_report}</p>}
                {f.structured_scores && <ScoreSummary scores={f.structured_scores} />}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 同意 */}
      {consent && (
        <section className="space-y-2" style={{ order: 20 }}>
          <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
            同意與公開權限
          </h2>
          <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 flex flex-wrap gap-2">
            <ConsentBadge on={consent.storage_consent} label="保存紀錄" />
            <ConsentBadge on={consent.followup_consent} label="追蹤聯絡" />
            <ConsentBadge on={consent.reviewer_access} label="Reviewer 查看" />
            <ConsentBadge on={consent.internal_anonymous_use} label="內部研究" />
            <ConsentBadge on={consent.public_anonymous_use} label="匿名公開" />
            <ConsentBadge on={consent.media_public_use} label="公開照片／影片" />
          </div>
        </section>
      )}

      {/* 施炁者主導的確認與公開流程：案主可隨時補充，但不再成為送審瓶頸。 */}
      {canPublishAnonymous && (
        <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3" style={{ order: 25 }}>
          <h2 className="text-base font-medium text-emerald-900" style={{ fontFamily: 'var(--font-heading)' }}>
            {isStaff ? 'ABAA 匿名公開' : '匿名公開設定'}
          </h2>
          <p className="text-xs text-emerald-800">
            匿名文字預設可送審及公開；案主已獲告知，並可隨時補充、更正、撤回或要求下架。只有露臉或可辨識的照片、影片需要案主逐檔同意。
          </p>
          <textarea
            value={publicSummary}
            onChange={(event) => setPublicSummary(event.target.value)}
            rows={4}
            placeholder="填寫完整但不含姓名、地址、聯絡方式的匿名案例摘要"
            className="w-full rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={handlePractitionerPublish}
            disabled={publishing}
            className="w-full sm:w-auto px-4 py-2 rounded-md bg-emerald-700 text-white text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {publishing ? '處理中…' : isStaff ? '核准並直接發布到見證牆' : '送交 ABAA／依既有核准直接發布'}
          </button>
          {isPractitionerConfirmed && qCase.status === 'under_review' && (
            <Link to="/og/qi-healing/review" className="inline-flex text-xs text-emerald-800 underline">前往 ABAA 覆核中心</Link>
          )}
        </section>
      )}

      {/* Reviewer 覆核 */}
      {review && (
        <section className="space-y-2" style={{ order: 30 }}>
          <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
            Reviewer 覆核
          </h2>
          <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-800">覆核狀態：{review.review_status}</span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900">Evidence：{review.evidence_level || '未定'}</span>
            </div>
            {review.public_summary && <p className="text-sm text-foreground-700">{review.public_summary}</p>}
          </div>
        </section>
      )}

      {/* 媒體逐檔授權 */}
      <section className="space-y-3" style={{ order: 70 }}>
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
            照片／影片／文件（逐檔授權）
          </h2>
          <label className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 text-background-50 text-xs hover:bg-accent-600 transition-colors cursor-pointer">
            <i className="ri-upload-line" />
            {uploading ? '上傳中…' : '上傳檔案'}
            <input type="file" multiple accept="image/*,video/*,audio/*,.aac,.acc,.adts,.m4a,.mp3,.wav,.ogg,.oga,.flac,.opus,.pdf" onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
        </div>
        <p className="text-xs text-foreground-400">原始檔一律存於私人空間。上傳不等於同意公開，每檔需分別選擇是否公開；含臉部或可辨識資訊的媒體須由案主本人授權公開。</p>
        <p className={`text-xs text-center py-2 ${mediaDragging ? 'text-accent-700 font-medium' : 'text-foreground-400'}`}>
          {mediaDragging ? '放開即可新增上傳' : '也可以把更多照片、影片或音訊直接拖放到這個框內'}
        </p>
        {media.length === 0 ? (
          <p className="text-sm text-foreground-400 bg-background-100 border border-foreground-200/50 rounded-xl p-4">尚無檔案。</p>
        ) : (
          <div className="space-y-3">
            {media.map((m) => (
              <MediaCard
                key={m.id}
                media={m}
                url={mediaUrls[m.id]}
                isRecipient={isRecipient}
                onPatch={(patch) => handleMediaPatch(m.id, patch)}
                onDelete={() => handleDeleteMedia(m.id)}
                deleting={deletingMediaId === m.id}
                mediaPublicConsent={consent?.media_public_use === true}
              />
            ))}
          </div>
        )}
      </section>

      {/* 案例治理彈窗：申請刪除 */}
      {showDeleteModal && (
        <ModalShell title="申請刪除整個案例" onClose={() => setShowDeleteModal(false)}>
          <div className="space-y-3">
            <div className="bg-background-100 border border-foreground-200/50 rounded-md p-3">
              <p className="text-xs text-foreground-500">案例編號</p>
              <p className="text-sm text-foreground-800 font-medium">{qCase.case_code || `案例 #${qCase.id}`}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 space-y-1.5">
              <p>刪除申請送出後需交由管理員審核，你無法自行永久刪除。</p>
              <p>此動作會影響：本案例的所有施炁紀錄（session）、後續追蹤（follow-up）、照片／影片／文件，以及已發布的公開見證。</p>
              <p>若案例已公開，送出申請的同時公開見證會暫停，等待審核結果。</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">刪除理由 <span className="text-rose-500">*</span></label>
              <select
                value={deleteReasonCategory}
                onChange={(e) => setDeleteReasonCategory(e.target.value)}
                className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800"
              >
                <option value="">請選擇理由</option>
                {DELETION_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">補充說明 <span className="text-rose-500">*</span></label>
              <textarea
                value={deleteReasonNote}
                onChange={(e) => setDeleteReasonNote(e.target.value)}
                rows={3}
                placeholder="請簡要說明刪除原因"
                className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 resize-none"
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-foreground-600 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-accent-500"
              />
              我理解此申請需經審核，申請期間案例會被鎖定，無法新增施炁。
            </label>
            {modalError && <p className="text-xs text-rose-600 bg-rose-50 rounded-md px-3 py-2">{modalError}</p>}
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-xs text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={handleRequestDeletion}
                disabled={governanceSubmitting}
                className="whitespace-nowrap px-4 py-2 rounded-md bg-rose-600 text-background-50 text-xs hover:bg-rose-700 transition-colors cursor-pointer disabled:opacity-50"
              >
                {governanceSubmitting ? '送出中…' : '確認申請刪除'}
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {/* 案例治理彈窗：提出異議 */}
      {showDisputeModal && (
        <ModalShell title="對此案例提出異議" onClose={() => setShowDisputeModal(false)}>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">爭議原因 <span className="text-rose-500">*</span></label>
              <textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                rows={3}
                placeholder="請說明為何提出異議"
                className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">哪一段資料有問題</label>
              <textarea
                value={disputeWhichData}
                onChange={(e) => setDisputeWhichData(e.target.value)}
                rows={2}
                placeholder="可留白"
                className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">希望如何處理</label>
              <textarea
                value={disputeResolution}
                onChange={(e) => setDisputeResolution(e.target.value)}
                rows={2}
                placeholder="可留白"
                className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 resize-none"
              />
            </div>
            <p className="text-xs text-foreground-500">提出異議後，案例公開會暫停、保留雙方原始內容，且不會讓任一方覆蓋或刪除另一方紀錄。</p>
            {modalError && <p className="text-xs text-rose-600 bg-rose-50 rounded-md px-3 py-2">{modalError}</p>}
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setShowDisputeModal(false)}
                className="whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-xs text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={handleDispute}
                disabled={governanceSubmitting}
                className="whitespace-nowrap px-4 py-2 rounded-md bg-rose-600 text-background-50 text-xs hover:bg-rose-700 transition-colors cursor-pointer disabled:opacity-50"
              >
                {governanceSubmitting ? '送出中…' : '送出異議'}
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

function NotificationStatus({ notifications }: { notifications: QiNotification[] }) {
  const latestInApp = notifications.find((n) => n.delivery_channel === 'in_app');
  const latestEmail = notifications.find((n) => n.delivery_channel === 'email');

  const label = (status: string): string => {
    const map: Record<string, string> = {
      not_created: '未建立',
      queued: '排隊中',
      sent: '已送達',
      delivered: '已送達',
      opened: '已開啟',
      completed: '已完成',
      failed: '寄送失敗',
      cancelled: '已取消',
    };
    return map[status] || status;
  };

  const cls = (status: string) => {
    if (['sent', 'delivered', 'opened', 'completed'].includes(status)) return 'bg-emerald-100 text-emerald-700';
    if (status === 'failed') return 'bg-amber-100 text-amber-700';
    return 'bg-background-200 text-foreground-500';
  };

  return (
    <div className="flex gap-2 flex-wrap">
      <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${latestInApp ? cls(latestInApp.status) : 'bg-background-200 text-foreground-400'}`}>
        站內通知：{latestInApp ? label(latestInApp.status) : '尚未建立'}
      </span>
      <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${latestEmail ? cls(latestEmail.status) : 'bg-background-200 text-foreground-400'}`}>
        Email：{latestEmail ? label(latestEmail.status) : '尚未寄送'}
      </span>
    </div>
  );
}

function FollowupBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed: '已完成',
    scheduled: '已排程',
    due: '待回報',
    notified: '已通知',
    opened: '已開啟',
    skipped: '已略過',
    overdue: '逾期',
    cancelled: '已取消',
    pending: '待追蹤',
  };
  const cls = status === 'completed' ? 'bg-emerald-100 text-emerald-700' : status === 'overdue' ? 'bg-rose-100 text-rose-700' : 'bg-background-200 text-foreground-500';
  return <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${cls}`}>{map[status] || status}</span>;
}

function MediaCard({
  media,
  url,
  isRecipient,
  onPatch,
  onDelete,
  deleting,
  mediaPublicConsent,
}: {
  media: QiMedia;
  url?: string;
  isRecipient: boolean;
  onPatch: (patch: { contains_face?: boolean; identifying_information?: string | null; internal_use_allowed?: boolean; public_use_allowed?: boolean; permission_granted_at?: string | null; permission_withdrawn_at?: string | null; description?: string | null; evidence_stage?: EvidenceStage | null }) => void;
  onDelete: () => void;
  deleting: boolean;
  mediaPublicConsent: boolean;
}) {
  const isFacePhotoOrVideo = media.contains_face && (media.file_type === 'image' || media.file_type === 'video');
  const publicBlocked = isFacePhotoOrVideo && !isRecipient;
  const isAudio = media.file_type === 'audio'
    || /\.(aac|acc|adts|m4a|mp3|wav|ogg|oga|flac|opus)$/i.test(media.file_path);

  return (
    <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-[260px_1fr] gap-4">
      <div className="bg-background-50 border border-foreground-200/50 rounded-lg overflow-hidden min-h-40">
        {url ? (
          media.file_type === 'image' ? (
            <img src={url} alt="施炁紀錄檔案" className="w-full h-40 object-contain" />
          ) : media.file_type === 'video' ? (
            <div className="space-y-1.5 p-2">
              <video src={url} controls preload="metadata" playsInline className="w-full h-32 bg-black rounded object-contain" />
              <a href={url} target="_blank" rel="noreferrer" className="block text-center text-[11px] text-accent-700 hover:underline">
                若瀏覽器無法播放，開啟原始檔
              </a>
            </div>
          ) : isAudio ? (
            <div className="w-full h-40 flex items-center px-3">
              <audio src={url} controls preload="metadata" className="w-full" />
            </div>
          ) : (
            <div className="w-full h-40 flex items-center justify-center">
              <i className="ri-file-text-line text-2xl text-foreground-400" />
            </div>
          )
        ) : (
          <div className="w-full h-40 flex items-center justify-center">
            <i className="ri-image-line text-2xl text-foreground-300" />
          </div>
        )}
      </div>

      <div className="space-y-2.5">
        <p className={`text-[11px] rounded px-2.5 py-1.5 ${media.public_use_allowed ? 'bg-emerald-50 text-emerald-700' : mediaPublicConsent ? 'bg-amber-50 text-amber-700' : 'bg-background-200 text-foreground-500'}`}>
          {media.public_use_allowed
            ? '✓ 此檔已被受施者指定同意公開'
            : mediaPublicConsent
              ? '受施者已同意逐檔指定；此檔尚未指定公開'
              : '受施者尚未同意公開此檔，僅私人保存'}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={media.evidence_stage || ''}
            onChange={(e) => onPatch({ evidence_stage: (e.target.value || null) as EvidenceStage | null })}
            className="px-2 py-1.5 rounded-md border border-foreground-200 bg-background-50 text-xs text-foreground-700"
          >
            <option value="">選擇階段</option>
            {EVIDENCE_STAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="text-[10px] text-foreground-400">{media.file_type}</span>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="ml-auto text-xs text-rose-600 hover:text-rose-700 cursor-pointer disabled:opacity-50"
          >
            {deleting ? '刪除中…' : '刪除附件'}
          </button>
        </div>

        <label className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer">
          <input
            type="checkbox"
            checked={media.contains_face}
            onChange={(e) => onPatch({ contains_face: e.target.checked })}
            className="w-4 h-4 accent-accent-500"
          />
          含臉部或可辨識人物
        </label>

        <input
          type="text"
          value={media.identifying_information || ''}
          onChange={(e) => onPatch({ identifying_information: e.target.value || null })}
          placeholder="其他可辨識資訊（可留白）"
          className="w-full px-2 py-1.5 rounded-md border border-foreground-200 bg-background-50 text-xs text-foreground-700"
        />

        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer">
            <input
              type="checkbox"
              checked={media.internal_use_allowed}
              onChange={(e) => onPatch({ internal_use_allowed: e.target.checked })}
              className="w-4 h-4 accent-accent-500"
            />
            內部使用
          </label>
          <label className={`flex items-center gap-2 text-xs cursor-pointer ${publicBlocked ? 'text-foreground-400' : 'text-foreground-600'}`}>
            <input
              type="checkbox"
              checked={media.public_use_allowed}
              disabled={publicBlocked}
              onChange={(e) => {
                if (e.target.checked) {
                  onPatch({ public_use_allowed: true, permission_withdrawn_at: null, permission_granted_at: new Date().toISOString() });
                } else {
                  onPatch({ public_use_allowed: false });
                }
              }}
              className="w-4 h-4 accent-accent-500"
            />
            公開使用
          </label>
          {media.public_use_allowed && (
            <button
              onClick={() => onPatch({ public_use_allowed: false, permission_withdrawn_at: new Date().toISOString() })}
              className="whitespace-nowrap text-xs text-rose-600 hover:text-rose-700 cursor-pointer"
            >
              撤回公開授權
            </button>
          )}
        </div>

        {publicBlocked && (
          <p className="text-[10px] text-amber-600">此照片／影片含可辨識臉部，僅案主本人可逐檔授權公開（施炁者不能代替同意）。</p>
        )}
        {media.permission_withdrawn_at && (
          <p className="text-[10px] text-rose-500">此檔公開授權已於 {formatDate(media.permission_withdrawn_at)} 撤回。</p>
        )}
      </div>
    </div>
  );
}

function TimelineSection({ title, records, order }: { title: string; records: QiSessionRecord[]; order?: number }) {
  if (records.length === 0) return null;
  return (
    <section className="space-y-2" style={{ order }}>
      <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
        {title}
      </h2>
      {records.map((r) => (
        <div key={r.id} className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 space-y-2">
          {r.narrative && <p className="text-sm text-foreground-700 leading-relaxed">{r.narrative}</p>}
          {r.structured_data && <StructuredDataView data={r.structured_data} />}
          <p className="text-[10px] text-foreground-400">
            紀錄日期：{formatDate(r.recorded_at || r.created_at)}
            {r.locked_at ? ' · 已封存' : ''}
          </p>
        </div>
      ))}
    </section>
  );
}

const FIELD_LABELS: Record<string, string> = {
  main_issue: '主要問題',
  body_parts: '身體部位',
  discomfort: '身體不適程度',
  emotion: '情緒狀態',
  function: '生活功能',
  life_function: '生活功能',
  history: '舊傷／過往事件',
  treatment: '現行療法',
  start: '開始時間',
  end: '結束時間',
  contact: '接觸方式',
  position: '施炁位置',
  audio: '音頻／設備',
  stopped: '是否中途停止',
  attention: '需注意反應',
  reactions: '當下反應',
  other: '其他',
  duration: '持續時間',
  recipient_note: '受施者描述',
  recipient_change: '本人感受到的變化',
  new_discomfort: '新不適',
  need_help: '需專業協助',
  next_step: '下一步建議',
  followup_24h: '24 小時內回報',
  outcome: '原有問題',
  new_reactions: '新反應',
  other_therapy: '其他療法',
  before: '施炁前狀況',
  during: '施炁當下感受',
  after: '施炁後變化',
};

function StructuredDataView({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0));
  if (entries.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
      {entries.map(([key, val]) => {
        const label = FIELD_LABELS[key] || key;
        let text: string;
        if (typeof val === 'boolean') text = val ? '是' : '否';
        else if (Array.isArray(val)) text = val.join('、');
        else if (typeof val === 'number') text = ['discomfort', 'emotion', 'function', 'life_function'].includes(key) ? `${val} / 10` : String(val);
        else text = String(val);
        return (
          <div key={key} className="text-xs">
            <span className="text-foreground-400">{label}：</span>
            <span className="text-foreground-700">{text || '—'}</span>
          </div>
        );
      })}
    </div>
  );
}

function ScoreSummary({ scores }: { scores: Record<string, unknown> }) {
  const keys = ['discomfort', 'emotion', 'life_function'] as const;
  return (
    <div className="flex gap-4 mt-2 flex-wrap">
      {keys.map((k) => {
        const v = scores[k];
        if (typeof v !== 'number') return null;
        return (
          <div key={k} className="text-xs text-foreground-500">
            {FIELD_LABELS[k]}：<span className="text-foreground-700 font-medium">{v} / 10</span>
          </div>
        );
      })}
    </div>
  );
}

function ConsentBadge({ on, label }: { on: boolean; label: string }) {
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full ${on ? 'bg-emerald-100 text-emerald-700' : 'bg-background-200 text-foreground-400'}`}>
      {on ? '✓ ' : ''}{label}
    </span>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-background-50 rounded-lg border border-foreground-200 p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-base font-medium text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-foreground-100 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}


function RevisionCard({ revision }: { revision: QiRevision }) {
  const before = revision.before ?? {};
  const after = revision.after ?? {};
  return (
    <article className="rounded-xl border border-amber-200/70 bg-amber-50/40 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium text-foreground-800">
            第 {revision.revision_number} 版修訂
          </p>
          <p className="text-xs text-foreground-500">
            {revision.change_reason || '系統保存的資料修訂'}
          </p>
        </div>
        <p className="text-[11px] text-foreground-400">{formatDate(revision.changed_at)}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RevisionSnapshot title="修改前（永久保存）" data={before} />
        <RevisionSnapshot title="修改後" data={after} />
      </div>
    </article>
  );
}

function RevisionSnapshot({ title, data }: { title: string; data: Record<string, unknown> }) {
  const visible = Object.fromEntries(
    Object.entries(data).filter(([key]) => !['id', 'case_id', 'created_by'].includes(key)),
  );
  return (
    <div className="rounded-lg border border-foreground-200/60 bg-background-50 p-3">
      <p className="mb-2 text-xs font-medium text-foreground-600">{title}</p>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed text-foreground-600">
        {JSON.stringify(visible, null, 2)}
      </pre>
    </div>
  );
}
